import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { COMPANY_STYLES } from "@/api";
import { Card } from "@/components/ui/card";
import { Star } from "lucide-react";

const HOST = process.env.REACT_APP_BACKEND_URL;

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [showOnly, setShowOnly] = useState(false);
  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (j) => {
    await api.patch(`/jobs/${j.id}`, { showcase: !j.showcase });
    toast.success(j.showcase ? "Removed from showcase" : "Marked as showcase");
    load();
  };

  const filtered = showOnly ? items.filter((i) => i.showcase) : items;

  return (
    <div className="space-y-5" data-testid="gallery-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Before / After Gallery</h1>
          <p className="text-sm text-muted-foreground">Field photos auto-collected from job checkpoints — star the best for marketing</p>
        </div>
        <button data-testid="showcase-filter" onClick={() => setShowOnly(!showOnly)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${showOnly ? "bg-warning text-inverse border-warning" : "bg-card hover:bg-accent"}`}>
          ★ Showcase only
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((j) => {
          const before = j.photos.find((p) => p.type === "arrived");
          const after = j.photos.find((p) => p.type === "job_completed");
          return (
            <Card key={j.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`gallery-card-${j.id}`}>
              <div className="grid grid-cols-2 gap-px bg-border">
                {[before, after].map((p, i) => (
                  <div key={i === 0 ? "before" : "after"} className="relative aspect-square bg-muted">
                    {p ? <img src={`${HOST}${p.url}`} alt={p.type} className="absolute inset-0 w-full h-full object-cover" />
                      : <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">No photo</div>}
                    <span className="absolute top-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-overlay text-inverse uppercase">{i === 0 ? "Before" : "After"}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{j.service}</div>
                  <div className="text-xs text-muted-foreground font-mono">{j.job_no}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${COMPANY_STYLES[j.company] || ""}`}>{j.company}</span>
                {j.rating && <span className="text-xs text-warning-foreground font-semibold">★ {j.rating}</span>}
                <button data-testid={`showcase-toggle-${j.id}`} onClick={() => toggle(j)} title="Toggle showcase"
                  className={`p-1.5 rounded-full transition-colors ${j.showcase ? "text-warning-foreground" : "text-muted-foreground hover:text-warning-foreground"}`}>
                  <Star size={18} fill={j.showcase ? "currentColor" : "none"} />
                </button>
              </div>
            </Card>
          );
        })}
      </div>
      {!filtered.length && <div className="text-center text-muted-foreground py-16 text-sm">No job photos yet — they appear here after field checkpoints with photos</div>}
    </div>
  );
}
